import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { BuildPriority } from '@hangar/shared';
import { useBuildMutations } from '@/api/hooks';
import type { Artifact, Build } from '@/api/schemas';
import { humanize } from '@/lib/format';
import { useSeesProviders } from '@/lib/useAudience';
import { RETRY_UNAVAILABLE, useCan, useFeature } from '@/lib/permissions';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { EnvPill, PlatformIcon, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { buildDuration, canCancel, canReprioritize, canRetry } from '../buildHelpers';
import { executionModeLabel } from '../buildLabels';
import { BuildStatusPill } from '../BuildStatusPill';
import { CancelBuildDialog } from './CancelBuildDialog';
import { InstallSheet } from './InstallSheet';
import { installCandidate, showInstall } from './installModel';
import { PRIORITIES, providerLinkName, providerShortName, retryDisabledReason, showCancelPending, statusPillLabel } from './headerModel';
import { useCancelAction, usePriorityAction, useRetryAction } from './useBuildHeaderActions';

/** Screen 03 header: back link, platform tile, ref, status + env + provider chips, actions (Install first when the build has an app file). */
export function BuildHeader({ build: b, projectSlug, now, artifacts }: { build: Build; projectSlug: string; now: number; artifacts?: Artifact[] }) {
  const { cancel, retry, priority } = useBuildMutations(b.id);
  const cancelAction = useCancelAction(cancel);
  const onRetry = useRetryAction(retry);
  const onPriority = usePriorityAction(priority, b.priority);
  return (
    <header className="enter flex flex-col gap-3">
      <Link to="/projects/$slug/builds" params={{ slug: projectSlug }} data-testid="build-back" className="inline-flex w-fit items-center gap-1 rounded-sm text-sm text-muted-foreground hover:text-foreground">
        <Icon name="arrow_back" size={18} />
        Back to Builds
      </Link>
      <TitleRow b={b} now={now} />
      {b.git.commitMessage ? <p className="text-sm text-muted-foreground">{b.git.commitMessage}</p> : null}
      <HeaderActions b={b} artifacts={artifacts} onAskCancel={() => cancelAction.setConfirmCancel(true)} retryPending={retry.isPending} priorityPending={priority.isPending} onRetry={() => void onRetry()} onPriority={(p) => void onPriority(p)} />
      <CancelBuildDialog
        build={b}
        open={cancelAction.confirmCancel}
        onOpenChange={cancelAction.setConfirmCancel}
        reason={cancelAction.reason}
        onReasonChange={cancelAction.setReason}
        onConfirm={() => void cancelAction.onCancel()}
      />
    </header>
  );
}

interface ActionsProps {
  b: Build;
  artifacts?: Artifact[];
  onAskCancel: () => void;
  retryPending: boolean;
  priorityPending: boolean;
  onRetry: () => void;
  onPriority: (p: BuildPriority) => void;
}

function HeaderActions({ b, artifacts, onAskCancel, retryPending, priorityPending, onRetry, onPriority }: ActionsProps) {
  const operator = useSeesProviders();
  return (
    <div className="flex flex-wrap items-center gap-2">
      {showInstall(b, artifacts) ? <InstallAction artifact={installCandidate(artifacts)} /> : null}
      {canCancel(b) ? <CancelButton onClick={onAskCancel} /> : null}
      {showCancelPending(b) ? <CancelPending /> : null}
      <RetryControl b={b} pending={retryPending} onRetry={onRetry} />
      {canReprioritize(b) ? <PriorityMenu b={b} pending={priorityPending} onPriority={onPriority} /> : null}
      {b.provider?.buildUrl && operator ? <ProviderLink url={b.provider.buildUrl} kind={b.providerAccount.kind} /> : null}
    </div>
  );
}

function TitleRow({ b, now }: { b: Build; now: number }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-lg border bg-card">
        <PlatformIcon platform={b.platform} size={24} />
      </span>
      <h1 className="min-w-0 font-mono text-2xl font-semibold tracking-tight text-foreground" data-testid="build-ref">
        {b.app.slug}
        <span className="text-subtle">#{b.number}</span>
      </h1>
      <BuildChips b={b} now={now} />
    </div>
  );
}

function BuildChips({ b, now }: { b: Build; now: number }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <BuildStatusPill status={b.status} label={statusPillLabel(b.status, buildDuration(b, now))} size="md" />
      <EnvPill env={b.environment} />
      <ProviderChip b={b} />
      <Tag className="font-sans text-muted-foreground">
        {executionModeLabel(b.executionMode)}
      </Tag>
      {b.attempt > 1 ? <Tag className="text-awaiting">attempt {b.attempt}</Tag> : null}
    </div>
  );
}

/** The provider account the build runs on: platform operators only (SPEC decision 23). */
function ProviderChip({ b }: { b: Build }) {
  if (!useSeesProviders()) return null;
  return (
    <span className="flex items-center gap-1.5 rounded-md border bg-secondary px-2 py-1 font-mono text-xs text-muted-foreground">
      <ProviderBadge kind={b.providerAccount.kind} className="border-0 bg-transparent px-0" />
      {providerShortName(b.providerAccount.name)}
    </span>
  );
}

/** Install: a QR code and a link to the build's install page (SPEC decision 28), for anyone who can read the build. */
function InstallAction({ artifact }: { artifact: Artifact | null }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="build-install" onClick={() => setOpen(true)}>
        <Icon name="qr_code_2" size={18} />
        Install
      </Button>
      <InstallSheet open={open} onOpenChange={setOpen} artifact={artifact} />
    </>
  );
}

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="destructive" data-testid="build-cancel" onClick={onClick}>
      <Icon name="cancel" size={18} />
      Cancel build
    </Button>
  );
}

function CancelPending() {
  return (
    <span className="flex items-center gap-1.5 text-xs text-awaiting">
      <Icon name="pending" size={16} />
      Cancel requested, waiting for the build service to stop
    </span>
  );
}

/** Retry: hidden for guests and clients; disabled with its reason without builds:write or when the API has no retry. */
function RetryControl({ b, pending, onRetry }: { b: Build; pending: boolean; onRetry: () => void }) {
  const canBuild = useCan('builds:write', b.projectId);
  const retryServed = useFeature('retry');
  const blockedReason = retryDisabledReason(b);
  if (canRetry(b)) {
    return (
      <GatedButton variant="outline" data-testid="build-retry" can={canBuild} blockedReason={retryServed === false ? RETRY_UNAVAILABLE : undefined} loading={pending} onClick={onRetry}>
        <Icon name="refresh" size={18} />
        Retry
      </GatedButton>
    );
  }
  return blockedReason && !canBuild.hidden ? <RetryBlocked reason={blockedReason} /> : null;
}

function RetryBlocked({ reason }: { reason: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" data-testid="build-retry" aria-disabled="true" onClick={(e) => e.preventDefault()}>
          <Icon name="refresh" size={18} />
          Retry
        </Button>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}

function PriorityMenu({ b, pending, onPriority }: { b: Build; pending: boolean; onPriority: (p: BuildPriority) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="build-priority" loading={pending}>
          <Icon name="low_priority" size={18} />
          Priority: {humanize(b.priority)}
          <Icon name="expand_more" size={16} className="text-subtle" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Queue priority</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={b.priority} onValueChange={(v) => onPriority(v as BuildPriority)}>
          {PRIORITIES.map((p) => (
            <DropdownMenuRadioItem key={p} value={p} data-testid={`build-priority-${p}`}>
              {humanize(p)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProviderLink({ url, kind }: { url: string; kind: string }) {
  return (
    <Button asChild variant="outline">
      <a href={url} target="_blank" rel="noreferrer" data-testid="build-provider-link">
        Open in {providerLinkName(kind)}
        <Icon name="open_in_new" size={16} />
      </a>
    </Button>
  );
}
